import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import LanguageSelector from '../../components/common/LanguageSelector';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function Terms() {
  const { t, i18n } = useTranslation(['terms', 'common']);

  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('terms', currentLang);
    updateSEOTags(seoConfig, currentLang);
  }, [i18n.language]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex justify-between items-start mb-8">
            <h1 className="text-3xl font-bold text-gray-800">{t('page.title')}</h1>
            <LanguageSelector />
          </div>
          
          <div className="prose prose-gray max-w-none">
            <div className="mb-8">
              <p className="text-gray-600 mb-6">
                <strong>{t('page.lastUpdated', { date: i18n.language === 'zh' ? '2024年12月' : 'December 2024' })}</strong>
              </p>
              <p className="text-gray-700 leading-relaxed">
                {t('page.intro')}
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('section1.title')}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('section1.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('section2.title')}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('section2.intro')}
              </p>
              <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
                <li>{t('section2.timer')}</li>
                <li>{t('section2.stopwatch')}</li>
                <li>{t('section2.worldclock')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('section3.title')}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('section3.intro')}
              </p>
              <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
                <li>{t('section3.item1')}</li>
                <li>{t('section3.item2')}</li>
                <li>{t('section3.item3')}</li>
                <li>{t('section3.item4')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('section4.title')}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('section4.intro')}
              </p>
              <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
                <li>{t('section4.item1')}</li>
                <li>{t('section4.item2')}</li>
                <li>{t('section4.item3')}</li>
                <li>{t('section4.item4')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('section5.title')}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('section5.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('section6.title')}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('section6.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('section7.title')}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('section7.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('section8.title')}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('section8.content')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('section9.title')}</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                {t('section9.intro')}
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>{t('section9.email')}</strong> 
                  <a 
                    href={`mailto:mu9347mu@gmail.com?subject=${encodeURIComponent(t('section9.emailSubject'))}`}
                    className="text-blue-600 hover:text-blue-700 ml-2"
                  >
                    mu9347mu@gmail.com
                  </a>
                </p>
              </div>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                {t('footer.copyright')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
